import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch } from '../hooks/reduxHooks';
import { renameProject } from '../ProjectSlice_Store';

interface RenameChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  currentName: string;
}

export default function RenameChatModal({ isOpen, onClose, projectId, currentName }: RenameChatModalProps) {
  const dispatch = useAppDispatch();
  const [name, setName] = useState(currentName);
  const [isSaving, setIsSaving] = useState(false);
  const prevOpenRef = useRef(false);

  // Reset input only on the open transition (isOpen goes false→true)
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setName(currentName);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, currentName]);

  // Document-level Escape handler so it works regardless of focus
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isSaving, onClose]);

  if (!isOpen) return null;

  const isSaveDisabled = !name.trim() || name.trim() === currentName || isSaving;

  const handleSave = async () => {
    if (isSaveDisabled) return;
    setIsSaving(true);
    try {
      await dispatch(renameProject({ projectId, name: name.trim() })).unwrap();
      onClose();
    } catch {
      // Keep modal open on error so user can retry
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  // Prevent closing while save is in-flight
  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onMouseDown={handleClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rename-chat-modal-title"
        onMouseDown={handleClose}
      >
        <div
          className="bg-white rounded-lg shadow-xl max-w-lg w-full"
          onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 id="rename-chat-modal-title" className="text-lg font-semibold text-gray-900">Rename chat</h2>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            placeholder="Chat name"
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaveDisabled}
            className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#234E81' }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
